import { Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '../../pipes/translate.pipe';

export interface BreadcrumbItem {
  label: string;
  route?: string;
  /** Runs instead of routing — for a crumb that returns to a sub-state of the page already open. */
  action?: () => void;
  icon?: string;
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  template: `
    @if (items().length) {
      <nav class="breadcrumb" aria-label="breadcrumb">
        @if (showBack()) {
          <button type="button" class="breadcrumb__back" (click)="back.emit()">
            <mat-icon>arrow_back</mat-icon>
            {{ 'common.back' | translate }}
          </button>
          <span class="breadcrumb__divider">|</span>
        }
        <ol>
          @for (item of items(); track item; let last = $last) {
            <li>
              @if (!last && item.route && !item.action) {
                <a [routerLink]="item.route">
                  @if (item.icon) {
                    <mat-icon>{{ item.icon }}</mat-icon>
                  }
                  {{ item.label | translate }}
                </a>
              }
              @if (!last && item.action) {
                <button type="button" class="breadcrumb__link" (click)="item.action()">
                  @if (item.icon) {
                    <mat-icon>{{ item.icon }}</mat-icon>
                  }
                  {{ item.label | translate }}
                </button>
              }
              @if (last || (!item.route && !item.action)) {
                <span>
                  @if (item.icon) {
                    <mat-icon>{{ item.icon }}</mat-icon>
                  }
                  {{ item.label | translate }}
                </span>
              }
              @if (!last) {
                <mat-icon class="breadcrumb__separator">chevron_right</mat-icon>
              }
            </li>
          }
        </ol>
      </nav>
    }
    `,
  styles: [`
    :host {
      display: block;
      /* Guards against a page whose own :host has a negative top margin
         (a full-bleed layout trick some pages use) painting over this bar. */
      position: relative;
      z-index: 1;
    }
    :host:not(:has(.breadcrumb)) {
      display: none;
    }
    .breadcrumb {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.125rem;
      padding: 0.5rem 2rem 0rem 1rem;
      margin-bottom: -0.375rem;
      background: transparent;
    }
    .breadcrumb__back {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      background: none;
      border: none;
      border-radius: 0.25rem;
      padding: 0.25rem 0.5rem;
      margin: -0.25rem 0;
      color: #06531a;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }
    .breadcrumb__back:hover, .breadcrumb__back:focus-visible {
      background-color: rgba(6, 83, 26, 0.08);
      text-decoration: underline;
    }
    .breadcrumb__back mat-icon { font-size: 1.125rem; width: 1.125rem; height: 1.125rem; }
    .breadcrumb__divider { color: #9e9e9e; margin: 0 0.625rem; font-weight: 300; }
    .breadcrumb ol {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .breadcrumb li {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
    }
    .breadcrumb a, .breadcrumb__link {
      color: #06531a;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.375rem;
      margin: -0.25rem -0.375rem;
      border-radius: 0.25rem;
      font-weight: 500;
      transition: background-color 0.15s ease;
    }
    .breadcrumb__link {
      background: none;
      border: none;
      font-size: inherit;
      font-family: inherit;
      cursor: pointer;
    }
    .breadcrumb a:hover, .breadcrumb a:focus-visible, .breadcrumb__link:hover, .breadcrumb__link:focus-visible {
      background-color: rgba(6, 83, 26, 0.08);
      text-decoration: underline;
    }
    .breadcrumb span { color: #424242; display: flex; align-items: center; gap: 0.25rem; font-weight: 600; }
    .breadcrumb li:last-child span { color: #424242; }
    .breadcrumb__separator { font-size: 1.125rem; width: 1.125rem; height: 1.125rem; line-height: 1.125rem; display: flex; align-items: center; justify-content: center; color: #9e9e9e; margin: 0 0.125rem; }
  `],
  imports: [RouterLink, MatIcon, TranslatePipe],
})
export class BreadcrumbComponent {
  readonly items = input<BreadcrumbItem[]>([]);
  /** Whether to render the "← Back |" control before the trail. */
  readonly showBack = input<boolean>(false);
  readonly back = output<void>();
}
